import { COLORS } from "../constants";
import {
  adjustHue,
  getScale,
  saturate as _saturate,
  transparentize as _transparentize,
  desaturate as _desaturate,
  hasBadContrast,
  getLuminance,
  lighten,
  darken,
  toRgba,
  parseToRgba,
} from "color2k";
import ColorThief from "colorthief";
import { Palette } from "../constants/palettes";

export type RGBArray = [number, number, number];

/**
 * Converts an RGB array to a CSS string representation.
 * @param rgb - The RGB array to convert.
 * @param opacity - Optional opacity value.
 * @returns A CSS string representation of the RGB array, using `rgb()`, or `rgba()` if an opacity value is provided.
 */
export const arrayToRgbString = (rgb: RGBArray, opacity?: number): string => {
  return opacity === undefined
    ? `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`
    : `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`;
};

/**
 * Transparentizes an input color.
 *
 * @param color - The color string to transparentize.
 * @param amount - The amount to increase the opacity of the color by, given as a decimal between 0 and 1.
 * @returns An RGBA color string.
 */
export const transparentize = (color: string, amount: number): string => {
  return toRgba(_transparentize(color, amount));
};

/**
 * Saturates a color.
 *
 * @param color - Input color
 * @param amount - The amount to saturate, given as a decimal between 0 and 1
 * @returns An RGBA color string.
 */
export const saturate = (color: string, amount: number): string => {
  return toRgba(_saturate(color, amount));
};

/**
 * Desaturates a color.
 *
 * @param color - Input color
 * @param amount - The amount to desaturate, given as a decimal between 0 and 1
 * @returns An RGBA color string.
 */
export const desaturate = (color: string, amount: number): string => {
  return toRgba(_desaturate(color, amount));
};

/**
 * Gets the dominant color of an image. Avoids issues with low contrast colors.
 * @reference https://github.com/lokesh/color-thief/issues/40
 * @param image - The image to get the dominant color from.
 * @returns A Promise that resolves to an RGB array.
 */
export function getDominantColor(image: HTMLImageElement): Promise<RGBArray>;
export function getDominantColor(image: string): Promise<RGBArray>;
export function getDominantColor(
  image: HTMLImageElement | string
): Promise<RGBArray> {
  if (image instanceof HTMLImageElement) {
    return Promise.resolve(getDominantColorFromImage(image));
  } else {
    return getDominantColorFromImageUrl(image);
  }
}

function getDominantColorFromImage(image: HTMLImageElement): RGBArray {
  try {
    const colorThief = new ColorThief();
    const baseColor = colorThief.getColor(image);
    return baseColor;
  } catch {
    return [255, 255, 255];
  }
}

function getDominantColorFromImageUrl(url: string): Promise<RGBArray> {
  return new Promise((resolve, reject) => {
    const imgElement = document.createElement("img");
    imgElement.crossOrigin = "anonymous";

    imgElement.onload = () => {
      try {
        const baseColor = new ColorThief().getColor(imgElement);
        resolve(baseColor);
      } catch {
        resolve([255, 255, 255]);
      }
    };

    imgElement.onerror = () => reject(new Error("Failed to load image"));
    imgElement.src = url;
  });
}

/**
 * Generates a complete color palette based on a primary color input.
 * The palette includes primary and secondary color scales with varying shades.
 *
 * @param primaryColor - The base RGB color array to generate the palette from
 * @returns A Palette object containing:
 *          - BRAND colors (primary, light primary, and secondary)
 *          - PRIMARY scale (7 shades from super dark to super light)
 *          - SECONDARY scale (7 shades from super dark to super light)
 *
 * The function:
 * 1. Creates a 12-color scale from white to the primary color to black
 * 2. Generates a complementary secondary color by shifting the hue 180 degrees
 * 3. Creates another 12-color scale for the secondary color
 * 4. Maps specific positions from these scales to create the final palette structure
 */
export const generatePalette = (baseColor: RGBArray): Palette => {
  const primaryRgba = arrayToRgbString(baseColor);

  const primaryScaleFunc = getScale("#FFFFFF", primaryRgba, "#000000");
  const primaryScale = Array.from({ length: 12 }, (_, i) => {
    const color = primaryScaleFunc(i / 11);
    return desaturate(color, 0.0085);
  });

  const adjustedPrimaryScale = primaryScale.map((color) =>
    saturate(adjustBrightnessForLuminance(color), 0.005)
  );

  const secondaryColor = toRgba(saturate(adjustHue(primaryRgba, 180), 1));

  const secondaryScaleFunc = getScale("#FFFFFF", secondaryColor, "#000000");
  const secondaryScale = Array.from({ length: 12 }, (_, i) => {
    const color = secondaryScaleFunc(i / 11);
    return desaturate(color, 0.1);
  });

  const adjustedSecondaryScale = secondaryScale.map((color) =>
    desaturate(adjustBrightnessForLuminance(color), 0.22)
  );

  const brandPrimary = darkenForContrast(
    saturate(adjustedPrimaryScale[5], 0.115)
  );

  return {
    BASE_COLOR: arrayToRgbString(baseColor),
    BRAND: {
      PRIMARY: brandPrimary,
      PRIMARY_LIGHT: lightenWithinRange(brandPrimary, 0.25, {
        min: 0.6,
        max: 0.8,
      }),
      SECONDARY: desaturate(adjustedSecondaryScale[4], 0.02),
    },
    PRIMARY: {
      //backgrounds
      PRIMARY_SUPER_DARK: lightenWithinRange(
        lightenForContrast(primaryScale[9]),
        -0.6,
        {
          min: 0.0,
          max: 0.02,
        }
      ),
      PRIMARY_DARK: lightenWithinRange(
        lightenForContrast(primaryScale[8]),
        -0.4,
        {
          min: 0.01,
          max: 0.02,
        }
      ),
      //interactive components
      PRIMARY_MEDIUM: lightenWithinRange(adjustedPrimaryScale[7], -0.2, {
        min: 0.0,
        max: 0.11,
      }),
      //borders and seperators
      PRIMARY_MEDIUM_LIGHT: lightenWithinRange(
        desaturate(adjustedPrimaryScale[6], 0.1),
        0.08
      ),
      //solid colors
      PRIMARY_LIGHT: lightenWithinRange(
        saturate(adjustedPrimaryScale[5], 0.1),
        0.16
      ),
      //accessible text
      PRIMARY_LIGHTEST: lightenWithinRange(
        saturate(adjustedPrimaryScale[3], 0.2),
        0.12,
        {
          min: 0.5,
          max: 0.9,
        }
      ),
      PRIMARY_SUPER_LIGHT: lightenWithinRange(adjustedPrimaryScale[1], 0.02, {
        min: 0.86,
        max: 0.98,
      }),
    },
    SECONDARY: {
      //backgrounds
      SECONDARY_SUPER_DARK: lightenWithinRange(
        lightenForContrast(secondaryScale[9]),
        -0.6,
        {
          min: 0.0,
          max: 0.02,
        }
      ),
      SECONDARY_DARK: lightenWithinRange(
        lightenForContrast(secondaryScale[8]),
        -0.4,
        {
          min: 0.01,
          max: 0.02,
        }
      ),
      // interactive components
      SECONDARY_MEDIUM: lightenWithinRange(adjustedSecondaryScale[7], -0.2, {
        min: 0.0,
        max: 0.11,
      }),
      //borders and seperators
      SECONDARY_MEDIUM_LIGHT: lightenWithinRange(
        adjustedSecondaryScale[6],
        0.1
      ),
      //solid colors
      SECONDARY_LIGHT: lightenWithinRange(
        saturate(adjustedSecondaryScale[5], 0.1),
        0.16
      ),
      //accessible text
      SECONDARY_LIGHTEST: lightenWithinRange(
        saturate(adjustedSecondaryScale[3], 0.2),
        0.12,
        {
          min: 0.5,
          max: 0.9,
        }
      ),
      SECONDARY_SUPER_LIGHT: lightenWithinRange(
        adjustedSecondaryScale[0],
        0.02,
        {
          min: 0.86,
          max: 0.98,
        }
      ),
    },
    SUCCESS: {
      SUCCESS_DARK: COLORS.SUCCESS.DARK,
      SUCCESS_MEDIUM: COLORS.SUCCESS.MEDIUM,
      SUCCESS_LIGHT: COLORS.SUCCESS.LIGHT,
      SUCCESS_SUPER_LIGHT: COLORS.SUCCESS.SUPER_LIGHT,
    },
    WARNING: {
      WARNING_DARK: COLORS.WARNING.DARK,
      WARNING_MEDIUM: COLORS.WARNING.MEDIUM,
      WARNING_LIGHT: COLORS.WARNING.LIGHT,
      WARNING_SUPER_LIGHT: COLORS.WARNING.SUPER_LIGHT,
    },
    DANGER: {
      DANGER_SUPER_DARK: COLORS.DANGER.SUPER_DARK,
      DANGER_DARK: COLORS.DANGER.DARK,
      DANGER_MEDIUM: COLORS.DANGER.MEDIUM,
      DANGER_LIGHT: COLORS.DANGER.LIGHT,
      DANGER_SUPER_LIGHT: COLORS.DANGER.SUPER_LIGHT,
    },
  };
};

const lightenWithinRange = (
  color: string,
  amount: number,
  range: { min: number; max: number } = { min: 0.03, max: 0.97 }
): string => {
  let adjustedColor = color;
  let currentLuminance = getLuminance(adjustedColor);

  // Validate range bounds
  if (range.min < 0) range.min = 0;
  if (range.max > 1) range.max = 1;
  if (range.min >= range.max) {
    throw new Error("Invalid range: min must be less than max");
  }

  // For lightening (positive amount)
  if (amount > 0) {
    // Calculate how much we can lighten before hitting max
    const remainingLuminance = range.max - currentLuminance;
    if (remainingLuminance <= 0) return toRgba(adjustedColor);

    // Scale the amount based on remaining room to lighten
    const scaledAmount = amount * (remainingLuminance / (1 - currentLuminance));
    adjustedColor = lighten(adjustedColor, scaledAmount);
  }
  // For darkening (negative amount)
  else if (amount < 0) {
    // Calculate how much we can darken before hitting min
    const remainingLuminance = currentLuminance - range.min;
    if (remainingLuminance <= 0) return toRgba(adjustedColor);

    // Scale the amount based on remaining room to darken
    const scaledAmount = amount * (remainingLuminance / currentLuminance);
    adjustedColor = darken(adjustedColor, Math.abs(scaledAmount));
  }

  // Verify final luminance is within bounds
  const finalLuminance = getLuminance(adjustedColor);
  if (finalLuminance > range.max) {
    adjustedColor = adjustBrightnessWithinRange(adjustedColor, range);
  } else if (finalLuminance < range.min) {
    adjustedColor = adjustBrightnessWithinRange(adjustedColor, range);
  }

  return toRgba(adjustedColor);
};

const adjustBrightnessWithinRange = (
  color: string,
  range: { min: number; max: number }
): string => {
  let adjustedColor = color;
  let luminance = getLuminance(adjustedColor);

  // Darken if too bright
  while (luminance > range.max) {
    adjustedColor = darken(adjustedColor, 0.05);
    luminance = getLuminance(adjustedColor);
  }

  // Lighten if too dark
  while (luminance < range.min) {
    adjustedColor = lighten(adjustedColor, 0.05);
    luminance = getLuminance(adjustedColor);
  }

  return toRgba(adjustedColor);
};

const lightenForContrast = (color: string): string => {
  while (hasBadContrast(color, "readable", COLORS.GRAYSCALE.GRAY_DARK)) {
    color = lighten(color, 0.01);
  }
  return toRgba(color);
};

const darkenForContrast = (color: string): string => {
  while (hasBadContrast(COLORS.GRAYSCALE.GRAY_SUPER_LIGHT, "readable", color)) {
    color = darken(color, 0.01);
  }
  return toRgba(color);
};

const adjustBrightnessForLuminance = (color: string): string => {
  let currentColor = color;
  let currentLuminance = getLuminance(toRgba(currentColor));
  let adjustmentAttempts = 0;
  const MAX_ATTEMPTS = 6;

  while (currentLuminance < 0.16 && adjustmentAttempts < MAX_ATTEMPTS) {
    adjustmentAttempts++;
    currentColor = lighten(currentColor, 0.1);
    currentLuminance = getLuminance(toRgba(currentColor));
  }

  while (currentLuminance > 0.6 && adjustmentAttempts < MAX_ATTEMPTS) {
    adjustmentAttempts++;
    currentColor = darken(currentColor, 0.1);
    currentLuminance = getLuminance(toRgba(currentColor));
  }

  return toRgba(currentColor);
};
